import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/utils/tailwind-utils";
import {
  IconCircleMinus,
  IconLoader,
  IconSquareRoundedCheckFilled,
  IconProgress,
} from "@tabler/icons-react";
import MOptionsMenu, { DropdownOption } from "./MOptionsMenu";

import styles from "./MListItem.module.css";

export interface ListItemAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  className?: string;
  isVisible?: boolean;
}

export interface ListItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof listItemVariants> {
  title: string;
  secondaryText?: string;
  emphasizedText?: string;
  emphasizedIcon?: React.ReactNode;
  tertiaryText?: string;
  tertiaryIcon?: React.ReactNode;
  tertiaryVariant?: "default" | "orange";
  icon?: React.ReactNode;
  img?: string;
  error?: string;
  onAction?: () => void;
  actions?: ListItemAction[];
  actionsRenderer?: (actions: ListItemAction[]) => React.ReactNode;
  variant?: "default" | "purple";
  iconPosition?: "default" | "offset";
}

const listItemVariants = cva(styles.root, {
  variants: {
    usage: {
      crud: "",
      "multi-selection": "",
      uploading: "",
      "processing-upload": "",
      "processed-upload": "",
    },
    type: {
      "1-line": "",
      "2-line": "",
      "3-line": "",
    },
    state: {
      default: "",
      selected: "",
    },
  },
  defaultVariants: {
    usage: "crud",
    type: "1-line",
    state: "default",
  },
});

// Icon components
export interface ListItemProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof listItemVariants> {
  title: string;
  secondaryText?: string;
  emphasizedText?: string;
  emphasizedIcon?: React.ReactNode;
  tertiaryText?: string;
  tertiaryIcon?: React.ReactNode;
  tertiaryVariant?: "default" | "orange";
  icon?: React.ReactNode;
  img?: string;
  error?: string;
  onAction?: () => void;
  actions?: ListItemAction[];
  actionsRenderer?: (actions: ListItemAction[]) => React.ReactNode;
}

const ListItem = React.forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      className,
      usage = "crud",
      type = "1-line",
      state = "default",
      title,
      variant = "default",
      secondaryText,
      emphasizedText,
      emphasizedIcon,
      tertiaryText,
      tertiaryIcon,
      tertiaryVariant = "default",
      icon,
      img,
      error,
      onAction,
      actions,
      actionsRenderer,
      iconPosition = "default",
      ...props
    },
    ref
  ) => {
    const renderLeftIcon = () => {
      if (icon) {
        const iconWithColor =
          variant === "purple" && React.isValidElement(icon)
            ? React.cloneElement(icon, { color: "var(--colour-text-primary)" })
            : icon;
        const iconStyle: React.CSSProperties =
          iconPosition === "offset"
            ? { position: "relative", top: "-19px" }
            : {};
        return (
          <div
            className="flex-shrink-0 mr-3 flex items-center"
            style={iconStyle}
          >
            {iconWithColor}
          </div>
        );
      }
      if (img) {
        const imgStyle: React.CSSProperties =
          iconPosition === "offset"
            ? { position: "relative", top: "-19px" }
            : {};
        return (
          <div
            className="flex-shrink-0 mr-3 flex items-center"
            style={imgStyle}
          >
            <img
              src={img}
              alt=""
              className="w-10 h-10 rounded-full object-cover"
            />
          </div>
        );
      }
      return null;
    };

    const renderRightIcon = () => {
      // Only render icons for non-action cases
      switch (usage) {
        case "multi-selection":
          return (
            <div
              className="flex items-center justify-center w-8 h-8 text-neutral-900"
              onClick={onAction}
            >
              <IconCircleMinus />
            </div>
          );

        case "processing-upload":
        case "processed-upload":
          return null;
        case "uploading":
          return (
            <div
              className="flex items-center justify-center w-8 h-8 text-neutral-900"
              onClick={onAction}
            >
              <IconProgress />
            </div>
          );

        default:
          return null;
      }
    };

    // Default renderer for actions using MOptionsMenu
    const defaultActionsRenderer = (actions: ListItemAction[]) => {
      const visibleActions = actions.filter(
        (action) => action.isVisible !== false
      );

      if (visibleActions.length === 0) return null;

      // Convert ListItemAction to DropdownOption format
      const dropdownOptions: DropdownOption[] = visibleActions.map(
        (action) => ({
          id: action.id,
          label: action.label,
          icon: action.icon || null,
          onClick: action.onClick,
          className: action.className,
          isVisible: action.isVisible,
        })
      );

      return <MOptionsMenu options={dropdownOptions} />;
    };

    const renderProcessingMessage = () => {
      if (usage === "processing-upload") {
        return (
          <div className="flex items-start gap-1">
            <IconLoader
              size={10}
              style={{ top: 4, position: "relative", color: "#D47F3A" }}
            />
            <span
              className="body-xs-bold"
              style={{
                background: "linear-gradient(90deg, #D47F3A 0%, #F5CAA7 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontWeight: 600,
                display: "inline-block",
                animation: "readingShimmer 2s ease-in-out infinite",
              }}
            >
              Processing
            </span>
          </div>
        );
      }
      if (usage === "processed-upload") {
        return (
          <div className="flex items-start gap-1 self-stretch">
            <IconSquareRoundedCheckFilled
              size={10}
              style={{ color: "#217B57", top: 4, position: "relative" }}
            />
            <span className="body-xs-bold" style={{ color: "#217B57" }}>
              Completed
            </span>
          </div>
        );
      }
      return null;
    };

    const renderSecondaryInfo = () => {
      const processingMessage = renderProcessingMessage();
      if (processingMessage) {
        return processingMessage;
      }

      return (
        <div className="flex flex-col items-start gap-2 self-stretch w-full mt-[2px]">
          <div className="flex self-stretch items-center gap-1.5 min-w-0">
            {secondaryText && (
              <span className="text-xs font-medium leading-[140%] tracking-[0.12px] text-[var(--colour-text-caption-n400)] whitespace-nowrap">
                {secondaryText}
              </span>
            )}
            {emphasizedIcon && (
              <span className="flex-shrink-0">{emphasizedIcon}</span>
            )}
            {emphasizedText && (
              <span className="text-xs font-bold leading-[140%] tracking-[0.12px] text-[var(--colour-text-default)] truncate">
                {emphasizedText}
              </span>
            )}
          </div>
          {tertiaryText && (
            <span
              className={cn(
                "w-full text-xs font-bold leading-[140%] tracking-[0.12px] mt-[2px]",
                tertiaryVariant === "orange"
                  ? styles.tertiaryOrange
                  : variant === "purple"
                  ? styles.purpleTertiaryText
                  : "text-[var(--colour-text-secondary)]"
              )}
            >
              {tertiaryIcon && (
                <span className="flex-shrink-0">{tertiaryIcon}</span>
              )}
              {tertiaryText}
            </span>
          )}
        </div>
      );
    };

    return (
      <div
        ref={ref}
        className={cn(listItemVariants({ usage, type, state }), className)}
        {...props}
      >
        <div className="flex w-full">
          {renderLeftIcon()}
          <div className="flex-grow min-w-0 overflow-hidden mr-2 content-container">
            <div
              className={cn(
                "overflow-hidden text-ellipsis whitespace-nowrap font-medium line-height-[140%] mb-[6px] text-base",
                variant === "purple"
                  ? styles.purpleText
                  : state === "selected"
                  ? "text-primary-indigo-600"
                  : "text-neutral-900"
              )}
            >
              {title}
            </div>
            {(type === "2-line" || type === "3-line") && renderSecondaryInfo()}
          </div>
          <div className="flex-shrink-0 ml-auto">
            {actionsRenderer || (actions && actions.length > 0)
              ? actionsRenderer
                ? actionsRenderer(actions)
                : defaultActionsRenderer(actions)
              : renderRightIcon()}
          </div>
        </div>
        {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
      </div>
    );
  }
);

ListItem.displayName = "ListItem";

// Add keyframes for shimmer/pulse animation
const style = document.createElement("style");
style.innerHTML = `
@keyframes readingShimmer {
  0% {
    filter: brightness(1);
    opacity: 1;
  }
  50% {
    filter: brightness(1.15);
    opacity: 0.9;
  }
  100% {
    filter: brightness(1);
    opacity: 1;
  }
}
`;
document.head.appendChild(style);

export { ListItem, listItemVariants };
